import React, {useEffect, useMemo, useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {
    Alert,
    Box,
    Button,
    Link,
    Typography
} from '@mui/material'
import {SearchTransferResult} from '../transferUtils'
import CheckIcon from '@mui/icons-material/Check'
import {DataGrid, GridColDef} from '@mui/x-data-grid'

type InReviewRows = {
    id: number,
    sku: string,
    quantity: number,
    date_requested: Date
}

type PendingSelectionRows = {
    id: number,
    sku: string,
    quantity: number,
    manager: string | null,
    tk: string
}


const RequestTransfer = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const [inReviewRows, setInReviewRows] = useState<InReviewRows[]>([])
    const [pendingSelectionRows, setPendingSelectionRows] = useState<PendingSelectionRows[]>([])
    const [alertOpen, setAlertOpen] = useState(false)
    const requestsInReviewData = location.state?.requestsInReview
    const pendingSelectionData = location.state?.pendingSelectionData

    const setUpTableRows = (table_type: string, t: SearchTransferResult[]) => {
        if (table_type === 'in-review') {
            let reviewRows: InReviewRows[] = []
            t.forEach((data, index) => {
                reviewRows.push({
                    id: data.transferRequestId,
                    sku: data.sku,
                    quantity: data.quantity,
                    date_requested: data.requestedAt
                })
            })
            setInReviewRows(reviewRows)
        }

        if (table_type === 'pending-selection') {
            let pendingSelectionRows: PendingSelectionRows[] = []
            t.forEach((data, index) => {
                pendingSelectionRows.push({
                    id: data.transferRequestId,
                    sku: data.sku,
                    quantity: data.quantity,
                    manager: data.approvedBy,
                    tk: data.toLocationId
                })
            })
            setPendingSelectionRows(pendingSelectionRows)
        }
    }

    useEffect(() => {
        if (requestsInReviewData) {
            setUpTableRows('in-review', requestsInReviewData)
        }
        if (pendingSelectionData) {
            setUpTableRows('pending-selection', pendingSelectionData)
        }
    }, [location.state])

    useEffect(() => {
        if (location.state?.submissionSuccess) {
            setAlertOpen(true)
        }
        return () => {
            setAlertOpen(false)
        }
    }, [location.state])

    const inReviewCols: GridColDef[] = [
        {field: 'id', headerName: 'ID', flex: 1},
        {field: 'sku', headerName: 'Item (SKU)', flex: 1},
        {field: 'quantity', headerName: 'Quantity', flex: 1},
        {field: 'date_requested', headerName: 'Date Requested', flex: 1}
    ]

    const pendingSelectionCols: GridColDef[] = [
        {field: 'id', headerName: 'ID', flex: 1},
        {field: 'sku', headerName: 'Item (SKU)', flex: 1},
        {field: 'quantity', headerName: 'Quantity', flex: 1},
        {field: 'manager', headerName: 'RM', flex: 1},
        {field: 'tk', headerName: 'Truck', flex: 1},
        {
            field: 'action', headerName: 'Action', flex: 1, renderCell: (params) => (
                <Link
                    component="button"
                    variant="body2"
                    onClick={() => {
                        navigate(`/transfer-requests/${params.row.id}/select-serials`, {
                            state: {
                                transferSerialRequest: params.row
                            }
                        })
                    }}
                >
                    Review
                </Link>
            )
        }
    ]


    return (
        <>
            {alertOpen && (
                <Alert
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '80%',
                        mx: 'auto',
                        alignItems: 'flex-start',
                        my: 1
                    }}
                    icon={<CheckIcon fontSize="inherit"/>} severity="success"
                    onClose={() => {
                        location.state.submissionSuccess = false
                        setAlertOpen(false)
                    }}>
                    {location.state?.reject ? 'Rejected Transfer Request' : 'Created Transfer Request successfully!'}
                </Alert>
            )}
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Typography variant="h3" component="h2" gutterBottom
                            sx={{flexGrow: 1, textAlign: 'center', mb: 2, mt: 2}}>
                    Transfer Requests
                </Typography>
            </Box>
            <Box sx={{display: 'flex', justifyContent: 'center', mb: 3}}>
                <Button variant="contained" onClick={() => navigate('/inventory/transfer-requests/create')}>
                    Request a Transfer
                </Button>
            </Box>
            <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', mb: 2}}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    gap: 2 // Adds space between the tables
                }}>
                    <Box sx={{width: '40%'}}>
                        <Typography sx={{textAlign: 'center', mb: 1}}>
                            Transfer Requests in Review
                        </Typography>
                        <div style={{height: 600, backgroundColor: 'whitesmoke'}}>
                            <DataGrid
                                rows={inReviewRows}
                                columns={inReviewCols}
                                disableRowSelectionOnClick
                            />
                        </div>
                    </Box>
                    <Box sx={{width: '40%'}}>
                        <Typography sx={{textAlign: 'center', mb: 1}}>
                            Awaiting Serial Selections
                        </Typography>
                        <div style={{height: 600, backgroundColor: 'whitesmoke'}}>
                            <DataGrid
                                rows={pendingSelectionRows}
                                columns={pendingSelectionCols}
                                disableRowSelectionOnClick
                            />
                        </div>
                    </Box>
                </Box>
            </Box>
        </>

    )
}

export default RequestTransfer
